import React, { FC, useCallback, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { ValidatorMap } from 'ia-react-core/~/interfaces/ValidatorMap';
import keys from 'ia-react-core/~/utils/accessors/keys';
import { ErrorMap } from 'ia-react-core/~/interfaces/ErrorMap';
import { Validator } from 'ia-react-core/~/interfaces/Validator';

export type DefaultValidators<Value> = {
  [validatorName: string]: (
    Validator<Value> |
    [validatorName: string, validator: Validator<Value>, options?: Record<string, unknown>]
  );
};

export interface WithValidatorsProps<Value, Validators> {
  /** Map of function by type to validation the form itself. */
  validators?: ValidatorMap<Value>;
  /** Function to add custom validation outside of validators. */
  validate?: (value: Value) => ErrorMap;
  defaultValidators?: Partial<Validators>;
}

export interface WithInternapsValidatorsProps<Value, Validators> extends Omit<WithValidatorsProps<Value, Validators>, 'validate'> {
  validate?: (value: Value) => { errors: ErrorMap; warnings: ErrorMap; cautions: ErrorMap };
}

const withValidators = <Value, Validators extends DefaultValidators<Value>>(WrappedComponent: FC<WithInternapsValidatorsProps<Value, Validators>>) => {
  const WithValidators = ({ validators = {}, validate, defaultValidators = {}, ...props }: WithValidatorsProps<Value, Validators> & Record<keyof Validators, unknown>) => {
    const defaultValidatorsNames = useMemo(() => keys(defaultValidators), [defaultValidators]);
    const activeDefaultValidators = useMemo(() => defaultValidatorsNames.reduce<ValidatorMap<Value>>((currentValidators, validatorName) => {
      const propsRecord = props as Record<keyof Validators, unknown>;
      if (typeof propsRecord[validatorName] !== 'undefined' && propsRecord[validatorName] !== false) {
        const validator = defaultValidators[validatorName];
        const validatorType = Array.isArray(validator) ? validator[0] : validatorName;
        const validatorOptions = {
          [validatorType]: propsRecord[validatorName],
          ...(Array.isArray(validator) ? validator[2] : {}),
        };
        const validatorFn: Validator<Value> = Array.isArray(validator) ? validator[1] : validator;

        if (typeof validatorFn === 'object') {
          currentValidators[validatorType as string] = validatorFn;
        } else {
          currentValidators[validatorType as string] = {
            validator: validatorFn,
            exclusive: Boolean(validatorFn.exclusive),
            options: validatorOptions,
          };
        }
      }

      return currentValidators;
    }, {}), [defaultValidators, defaultValidatorsNames, props]);

    const combinedValidators = useMemo(() => ({
      ...activeDefaultValidators,
      ...validators,
    }), [activeDefaultValidators, validators]);

    const validateValidators = useCallback((value: Value) => {
      const errors = (validate && validate(value)) || {};

      const validatorsKeys = keys(combinedValidators);

      const result = validatorsKeys.reduce<{ errors: ErrorMap; warnings: ErrorMap; cautions: ErrorMap }>((currentErrors, validatorName) => {
        const validatorConfig = combinedValidators[validatorName];
        const { validator, options } = (typeof validatorConfig === 'function') ?
          { validator: validatorConfig, options: {} } :
          validatorConfig;

        const type = ('type' in validatorConfig && (validatorConfig.type === 'warning' || validatorConfig.type === 'caution')) ?
          `${validatorConfig.type}s` as const :
          'errors';

        const { [validatorName]: currentError, ...otherErrors } = currentErrors[type];

        const validatorResult = validator(value, options);

        currentErrors[type] = {
          ...otherErrors,
          [validatorName]: typeof validatorResult === 'string' ? validatorResult : !validatorResult,
        };

        return currentErrors;
      }, { errors, warnings: {}, cautions: {} });

      return result;
    }, [combinedValidators, validate]);

    return (
      <WrappedComponent
        validate={validateValidators}
        validators={combinedValidators}
        {...props}
      />
    );
  };

  return hoistNonReactStatics(WithValidators, WrappedComponent);
};

export default withValidators;
